import React from 'react';
import { Handle, Position } from 'reactflow';
import { BrainCircuit, Zap, Wrench } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * AutonomousNode — the visual canvas component for the Autonomous Engine node.
 *
 * Visually distinct from all other nodes:
 *   - Orange accent (matching marketplace brand)
 *   - Animated "thinking" pulse ring on the icon
 *   - Shows how many tools are wired via outgoing edges (via data.toolCount)
 *   - Single target handle (input) + single source handle (output)
 *
 * NOTE: "Tools" are wired by drawing edges FROM this node TO other nodes on
 * the canvas. The backend engine automatically discovers those target nodes
 * as callable tools when execution reaches this node.
 */
export default function AutonomousNode({ data, selected }) {
  const { isDark } = useTheme();

  const ORANGE = '#F97316';
  const ORANGE_DIM = 'rgba(249,115,22,0.15)';
  const ORANGE_BORDER = selected ? ORANGE : 'rgba(249,115,22,0.55)';

  const bg     = isDark ? '#0d1117' : '#ffffff';
  const text1  = isDark ? '#e6edf3' : '#111827';
  const text2  = isDark ? '#8b949e' : '#6b7280';

  const toolCount = data.toolCount || 0;
  const model     = data.config?.model || 'gpt-4o';
  const maxSteps  = data.config?.max_steps || 5;

  return (
    <div
      style={{
        background: bg,
        border: `2px solid ${ORANGE_BORDER}`,
        borderRadius: 14,
        padding: '12px 16px',
        minWidth: 200,
        position: 'relative',
        boxShadow: selected
          ? `0 0 0 3px rgba(249,115,22,0.30), 0 6px 24px rgba(249,115,22,0.15)`
          : `0 2px 10px rgba(0,0,0,0.12)`,
        transition: 'all 0.18s ease',
        cursor: 'default',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: 11,
          height: 11,
          background: ORANGE,
          border: `2px solid ${isDark ? '#0d1117' : '#ffffff'}`,
          left: -6,
        }}
      />

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        {/* Animated icon container */}
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: ORANGE_DIM,
            border: `1.5px solid rgba(249,115,22,0.4)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <BrainCircuit size={17} color={ORANGE} />
          {/* Pulse ring — animates when node is selected / running */}
          <span
            style={{
              position: 'absolute',
              inset: -3,
              borderRadius: 13,
              border: `1.5px solid ${ORANGE}`,
              opacity: selected ? 1 : 0,
              animation: selected ? 'autonomousPulse 1.6s ease-in-out infinite' : 'none',
              pointerEvents: 'none',
            }}
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Category badge */}
          <div
            style={{
              fontSize: 9,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: ORANGE,
              marginBottom: 2,
            }}
          >
            Autonomous Agent
          </div>
          {/* Label */}
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: text1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {data.label || 'Autonomous Agent'}
          </div>
        </div>
      </div>

      {/* Info pills row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        {/* Model badge */}
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            padding: '2px 7px',
            borderRadius: 100,
            background: isDark ? '#161b22' : '#f3f4f6',
            color: text2,
            border: `1px solid ${isDark ? '#21262d' : '#e5e7eb'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Zap size={8} color={ORANGE} />
          {model}
        </span>

        {/* Steps badge */}
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            padding: '2px 7px',
            borderRadius: 100,
            background: isDark ? '#161b22' : '#f3f4f6',
            color: text2,
            border: `1px solid ${isDark ? '#21262d' : '#e5e7eb'}`,
          }}
        >
          ≤{maxSteps} steps
        </span>

        {/* Tool count badge */}
        {toolCount > 0 && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 100,
              background: 'rgba(249,115,22,0.10)',
              color: ORANGE,
              border: '1px solid rgba(249,115,22,0.30)',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Wrench size={8} color={ORANGE} />
            {toolCount} tool{toolCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{
          width: 11,
          height: 11,
          background: ORANGE,
          border: `2px solid ${isDark ? '#0d1117' : '#ffffff'}`,
          right: -6,
        }}
      />

      {/* Keyframes — injected once */}
      <style>{`
        @keyframes autonomousPulse {
          0%   { transform: scale(1);    opacity: 0.9; }
          50%  { transform: scale(1.22); opacity: 0.2; }
          100% { transform: scale(1);    opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}
